import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  createBlogPost,
  deleteBlogPost,
  getBlogPost,
  listBlogCategories,
  listBlogDestinations,
  listBlogThemes,
  listBlogPosts,
  publishBlogPost,
  unpublishBlogPost,
  updateBlogPost,
  type BlogListQuery,
} from './blog.api';

import type { UpdateWebsiteBlogPostRequest } from '@utazzo/shared';

const KEY = 'website-blog';

export const useBlogPostsList = (query: BlogListQuery) =>
  useQuery({
    queryKey: [KEY, 'list', query],
    queryFn: () => listBlogPosts(query),
    staleTime: 10_000,
  });

export const useBlogPost = (id: string | null) =>
  useQuery({
    queryKey: [KEY, 'detail', id],
    queryFn: () => getBlogPost(id as string),
    enabled: !!id,
  });

export const useBlogCategories = () =>
  useQuery({
    queryKey: [KEY, 'categories'],
    queryFn: listBlogCategories,
    staleTime: 5 * 60_000,
  });

export const useBlogDestinations = () =>
  useQuery({
    queryKey: [KEY, 'destinations'],
    queryFn: listBlogDestinations,
    staleTime: 5 * 60_000,
  });

export const useBlogThemes = () =>
  useQuery({
    queryKey: [KEY, 'themes'],
    queryFn: listBlogThemes,
    staleTime: 5 * 60_000,
  });

export const useCreateBlogPost = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createBlogPost,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Blog post created');
    },
  });
};

export const useUpdateBlogPost = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWebsiteBlogPostRequest }) =>
      updateBlogPost(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Blog post saved');
    },
  });
};

export const usePublishBlogPost = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: publishBlogPost,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Blog post published');
    },
  });
};

export const useUnpublishBlogPost = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: unpublishBlogPost,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Blog post unpublished');
    },
  });
};

export const useDeleteBlogPost = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteBlogPost,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Blog post deleted');
    },
  });
};
